//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, SearchBar, Select, SelectItem } from 'unison';

import { CuralateInstagramFeature } from '@account-settings/redux/facebook-instagram/curalateFbIgFeatureInfoModel';
import { useGetInstagramFeature } from '@account-settings/redux/facebook-instagram/hooks/useFbIgFeatures';
import { getHasMultiHandleInstagramEnabled } from '@account-settings/redux/facebook-instagram/selectors/getMultiHandleFeature';
import LoadingBar from '@components/LoadingBar';
import { Alert } from '@components/atoms/alert/Alert';
import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import { HelpIconWithMargin } from '@components/atoms/help-icon/HelpIcon';
import TextInput from '@components/atoms/text-input/TextInput';
import DropdownSelect from '@components/molecules/dropdown-select/DropdownSelect';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import {
  useHashtagsUsageByAccountsQuery,
  useMultiHandleHashtagsUsageQuery,
  useTrackHashtagMutation,
} from '@hashtags/queries/hashtagsQueries';
import { findInstagramBusinessAccountForHashtagTracking } from '@hashtags/utils/instagramHashtagAccountUtils';
import { useGetInstagramConnectedAccountsQuery } from '@media-library/queries';
import { getIsMediaLibraryStreamsEnabled } from '@media-library/redux/getMediaLibraryPage';
import { streamCreationActions } from '@streams/actions/streamCreationActions';
import InstagramLinkoutButton from '@streams/components/InstagramLinkoutButton';
import { StreamSourcesList } from '@streams/components/StreamSourcesList';
import {
  GRAPH_API_LEARN_MORE_LINK,
  StreamSourcePillType,
  StreamSourceTypeLabels,
  StreamSourceTypes,
  streamSourceTypeToContentSourceType,
} from '@streams/constants/streamCreationConstants';
import { IStreamCreationErrors } from '@streams/models/streamCreationState';
import { getShouldNotAllowUgcStreams } from '@streams/selectors/getShouldNotAllowUgcStreams';
import { Mixins } from '@theme/mixins';
import { isDefined, isTruthy } from '@utils/filterPredicates';

import { GRAPH_API_HASHTAG_LIMIT } from 'app/constants/graph-api-hashtag-limit';

interface ISourceTypeSelectItem {
  label: string;
  value: StreamSourceTypes;
}

interface IOwnProps {
  isCreatingStream: boolean;
}

type streamLabels = keyof typeof StreamSourceTypeLabels;
const StreamCreationSourceTypes: streamLabels[] = [
  StreamSourceTypes.HASHTAG,
  StreamSourceTypes.USER,
  StreamSourceTypes.MENTIONS_AND_TAGS,
];

const StreamsSourceSelector = () => {
  const addedSources = useSelector(
    (state) => state.streams.streamCreation.sourcesAdded,
  );
  const areSocialDashboardsEnabled = useSelector(
    (state) => state.session.features.social_dashboards,
  );
  const brandInstagramUsername = useSelector(
    (state) => state.streams.streamsDrawer.brandInstagramUsername as string,
  );
  const errors = useSelector((state) => state.streams.streamCreation.errors);
  const hasOutstandingSaveRequest = useSelector(
    (state) => state.streams.streamCreation.hasOutstandingSaveRequest,
  );
  const isLoadingSources = useSelector(
    (state) => state.streams.streamCreation.isLoadingSources,
  );
  const isShareV2Vendor = useSelector(
    (state) => state.session.client.isShareV2Vendor,
  );
  const { sourceType, sourceValue } = useSelector(
    (state) => state.streams.streamCreation.sourceSelector,
  );
  const excludedSources = useSelector(
    (state) => state.streams.streamCreation.exclusionSourcesAdded,
  );
  const isMediaLibraryStreamsEnabled = useSelector(
    getIsMediaLibraryStreamsEnabled,
  );
  /**
   * Any valid connected IG business account can track hashtags,
   * so this check is sufficient to gate both the brand account
   * and hashtag related stream sources.
   */
  const isGraphApiConnected = useGetInstagramFeature(
    CuralateInstagramFeature.InstagramHashtags,
  );

  const shouldNotAllowUgcStreams = useSelector(getShouldNotAllowUgcStreams);
  const { data: connectedInstagramAccounts } =
    useGetInstagramConnectedAccountsQuery();

  const isMultiHandleFeatureEnabled = useSelector(
    getHasMultiHandleInstagramEnabled,
  );

  const { data: hashtagsUsageData } = useMultiHandleHashtagsUsageQuery();
  const { data: hashtagsByAccount } = useHashtagsUsageByAccountsQuery(
    connectedInstagramAccounts,
  );
  const { mutate: trackHashtag } = useTrackHashtagMutation();

  const connectedAccountsCount = connectedInstagramAccounts?.length ?? 0;
  const maxLimit = isMultiHandleFeatureEnabled
    ? GRAPH_API_HASHTAG_LIMIT * connectedAccountsCount
    : GRAPH_API_HASHTAG_LIMIT;

  const totalUsedHashtagCount =
    hashtagsByAccount?.reduce<number>(
      (sum, account) => sum + account.usedHashtagCount,
      0,
    ) ?? 0;

  const [selectedAccount, setSelectedAccount] = useState<string>(
    brandInstagramUsername,
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (shouldNotAllowUgcStreams) {
      void dispatch(
        streamCreationActions.changeSourceType(StreamSourceTypes.USER),
      );

      if (brandInstagramUsername && isGraphApiConnected) {
        void dispatch(
          streamCreationActions.changeSourceValue(brandInstagramUsername),
        );
      }
    }
  }, []);

  // Connect allows vendors to track users regardless of whether they have the Graph API connected.
  const isUsernameSelectedAsConnectVendor =
    sourceType === StreamSourceTypes.USER && isShareV2Vendor;
  const isMentionsSelected = sourceType === StreamSourceTypes.MENTIONS_AND_TAGS;
  const shouldDisableInput =
    isMentionsSelected ||
    (!isGraphApiConnected && !isUsernameSelectedAsConnectVendor) ||
    shouldNotAllowUgcStreams;

  let disabledInputTooltip = '';
  if (shouldDisableInput) {
    if (isMentionsSelected && isGraphApiConnected) {
      // Even if the Graph API is connected, we don't allow clients to select a user
      // other than their brand account when tracking Mentions.
      disabledInputTooltip = `You can only add mentions and tags from your brand's connected Instagram Business Account.`;
    } else if (isShareV2Vendor && !shouldNotAllowUgcStreams) {
      // As a special exception we allow Connect vendors to track Instagram Users even without
      // a Graph API connection, but show this error for other source types when Graph API is missing.
      disabledInputTooltip =
        'You must connect an Instagram Business Account to add this source type.';
    } else if (shouldNotAllowUgcStreams && isGraphApiConnected) {
      disabledInputTooltip =
        'You can only add your brand Instagram Business Account.';
    } else if (!isGraphApiConnected) {
      // Other than those special cases, show this error when the input is disabled
      // because a Graph API connection missing.
      disabledInputTooltip =
        'You must connect an Instagram Business Account to add sources.';
    }
  }

  let placeholderText = '';
  let value = sourceValue;

  switch (sourceType) {
    case StreamSourceTypes.HASHTAG:
      placeholderText = 'Enter a hashtag';
      value = isGraphApiConnected ? value : 'No Account Connected';
      break;
    case StreamSourceTypes.USER:
      placeholderText = 'Enter a username';
      value =
        isGraphApiConnected || isShareV2Vendor ? value : 'No Account Connected';
      break;
    case StreamSourceTypes.MENTIONS_AND_TAGS:
      value = isGraphApiConnected
        ? `@${brandInstagramUsername}`
        : 'No Account Connected';
      break;
    default:
      placeholderText = 'Enter a username or hashtag';
      value = isGraphApiConnected ? value : 'No Account Connected';
  }

  const shouldDisableSourceTypeSelector =
    (!isGraphApiConnected && !isShareV2Vendor) || shouldNotAllowUgcStreams;
  const hasSourceErrors = errors.sourceError || errors.sourceTypeError;

  const contentSourceType = streamSourceTypeToContentSourceType(sourceType);
  const sourceTypeSelectItems = computeSourceTypeSelectItems();

  const checkIfAlreadyUsedHashtag = (newHashtag: string) => {
    if (!hashtagsUsageData) {
      return false;
    }

    const normalizedHashtagName = newHashtag.toLowerCase();
    // Check if the hashtag exists in the array
    return hashtagsUsageData.trackedHashtags.some(
      (hashtag) => hashtag.hashtag.toLowerCase() === normalizedHashtagName,
    );
  };

  const getUnusedAddedHashtagCount = () => {
    if (!hashtagsUsageData) {
      return 0;
    }

    const usedHashtagsLowercase = hashtagsUsageData.trackedHashtags.map(
      (hashtag) => hashtag.hashtag.toLowerCase(),
    );

    const addedHashtags = addedSources
      .filter((source) => source.sourceType === StreamSourceTypes.HASHTAG)
      .map((source) => source.sourceValue.toLowerCase());

    const newAddedHashtags = addedHashtags.filter(
      (hashtag) => !usedHashtagsLowercase.includes(hashtag),
    );

    return newAddedHashtags.length;
  };

  const unusedAddedHashtagCountFromQuery = getUnusedAddedHashtagCount();
  const trackedHashtagCountWithUpdate =
    totalUsedHashtagCount + unusedAddedHashtagCountFromQuery;

  const remainingHashtagCount = maxLimit - trackedHashtagCountWithUpdate;
  const isAtHashtagLimit = trackedHashtagCountWithUpdate >= maxLimit;

  const checkIfDuplicateHashtag = (hashtagValue: string) => {
    return addedSources.some(
      (source) =>
        source.sourceType === StreamSourceTypes.HASHTAG &&
        source.sourceValue.toLowerCase() === hashtagValue.toLowerCase(),
    );
  };

  const checkIfExcludedHashtag = (hashtagValue: string) => {
    return excludedSources.some(
      (source) =>
        source.sourceType === StreamSourceTypes.HASHTAG &&
        source.sourceValue?.toLowerCase() === hashtagValue.toLowerCase(),
    );
  };

  const handleSourceValueChange = (value: string) => {
    void dispatch(streamCreationActions.changeSourceValue(value));
  };

  const handleAdd = () => {
    if (sourceValue || sourceType === StreamSourceTypes.MENTIONS_AND_TAGS) {
      if (sourceType === StreamSourceTypes.HASHTAG) {
        // Check if hashtag is already added to the stream
        const isDuplicateHashtag = checkIfDuplicateHashtag(sourceValue);

        if (isDuplicateHashtag) {
          void dispatch(
            streamCreationActions.setSourceError(
              'You have already added this hashtag',
            ),
          );
          return;
        }

        if (
          isMediaLibraryStreamsEnabled &&
          checkIfExcludedHashtag(sourceValue)
        ) {
          void dispatch(
            streamCreationActions.setSourceError(
              'This hashtag is already in the exclude list',
            ),
          );
          return;
        }

        const addHashtagToSources = () => {
          //TODO: PD-284564 - migrate to react-query
          void dispatch(
            streamCreationActions.directAddSource(
              StreamSourceTypes.HASHTAG,
              sourceValue,
            ),
          );
        };

        if (isMultiHandleFeatureEnabled) {
          const isAlreadyTracked = checkIfAlreadyUsedHashtag(sourceValue);
          if (isAlreadyTracked) {
            addHashtagToSources();
            return;
          }
          const businessAccountId = hashtagsByAccount?.length
            ? findInstagramBusinessAccountForHashtagTracking(hashtagsByAccount)
            : undefined;
          if (!businessAccountId) {
            void dispatch(
              streamCreationActions.setSourceError(
                'Unable to track hashtag. Please try again or contact support.',
              ),
            );
            return;
          }

          trackHashtag(
            { businessAccountId, hashtagName: sourceValue },
            { onSuccess: addHashtagToSources },
          );
          return;
        }

        addHashtagToSources();
        return;
      }

      // For non-hashtag source types, use the regular flow
      if (isMultiHandleFeatureEnabled) {
        const selectedAccountUserId = connectedInstagramAccounts?.find(
          (account) => account.username === selectedAccount,
        )?.userId;
        void dispatch(
          streamCreationActions.addSelectedSource(
            selectedAccount,
            selectedAccountUserId,
          ),
        );
      } else {
        void dispatch(streamCreationActions.addSelectedSource());
      }
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !shouldDisableHashtagAdd) {
      handleAdd();
    }
  };

  const handleSelect = (userName: string) => {
    setSelectedAccount(userName);
    void dispatch(streamCreationActions.changeSourceValue(userName));
  };

  // Disable adding hashtags if currently used count + newly added count will put them over the API limit
  // which is now calculated across all connected Instagram accounts using the useHashtagTracking hook
  //
  // Note that we allow adding a hashtag if it is already tracked,
  // since these hashtags won't require increasing the used hashtag count.
  const shouldDisableHashtagAdd =
    sourceType === StreamSourceTypes.HASHTAG &&
    isAtHashtagLimit &&
    !checkIfAlreadyUsedHashtag(sourceValue);

  const shouldShowHashtagLimitAlert =
    maxLimit > 0 &&
    remainingHashtagCount <= 5 &&
    remainingHashtagCount >= 0 &&
    !shouldNotAllowUgcStreams;

  // If client is a Connect vendor without a Graph API, ensure 'user' is the initial selected sourceType.
  if (
    !isGraphApiConnected &&
    isShareV2Vendor &&
    !shouldNotAllowUgcStreams &&
    sourceType === undefined
  ) {
    void dispatch(
      streamCreationActions.changeSourceType(StreamSourceTypes.USER),
    );
    return null;
  }

  return (
    <>
      <AddContentHeader>
        Add Content to Stream
        <HelpIconWithMargin helpText='Add sources below to automatically add user-generated, influencer, and owned content to this Stream (Instagram Only).' />
      </AddContentHeader>
      <div className='cu-d-flex cu-mb-2'>
        <StreamSourceSelectWrapper className='cu-mr-2'>
          <TypeHeader>Type</TypeHeader>
          <SmallTooltip
            content={
              <div>
                {shouldNotAllowUgcStreams
                  ? 'You can only add your brand Instagram Business Account.'
                  : 'You must connect an Instagram Business Account to add sources.'}
                {!shouldNotAllowUgcStreams && (
                  <PrimaryAnchor
                    className='cu-text-link-primary'
                    href={GRAPH_API_LEARN_MORE_LINK}
                    target='_blank'
                  >
                    &nbsp;Learn More
                  </PrimaryAnchor>
                )}
              </div>
            }
            isDisabled={!shouldDisableSourceTypeSelector}
            target={
              <TooltipTargetContainer>
                <Select
                  className='cu-w-100'
                  handleChange={(value) =>
                    void dispatch(streamCreationActions.changeSourceType(value))
                  }
                  isDisabled={
                    hasOutstandingSaveRequest || shouldDisableSourceTypeSelector
                  }
                  items={sourceTypeSelectItems}
                  unselectedLabel='Select a Source Type'
                  value={sourceType}
                >
                  {renderSelectItems(sourceTypeSelectItems)}
                </Select>
              </TooltipTargetContainer>
            }
          />
        </StreamSourceSelectWrapper>
        <StreamValueInputWrapper>
          <SourceValueContainer>
            <SourceHeader>
              Source
              {isDefined(contentSourceType) &&
                isTruthy(value) &&
                !shouldDisableInput && (
                  <InstagramLinkoutButton
                    sourceName={value}
                    sourceType={contentSourceType}
                  />
                )}
            </SourceHeader>
            {isMentionsSelected && isMultiHandleFeatureEnabled ? (
              <MultiHandleTargetContainer>
                <DropdownSelect
                  handleSelect={(value) => {
                    handleSelect(value);
                  }}
                  items={(connectedInstagramAccounts ?? []).map((account) => {
                    return {
                      value: account.username,
                      label: account.username,
                    };
                  })}
                  maxHeight={300}
                  placeholderText={'Select an account'}
                  value={selectedAccount}
                />
              </MultiHandleTargetContainer>
            ) : (
              <StyledSearchBar>
                <FullWidthInputWrapper>
                  <SmallTooltip
                    content={
                      <div>
                        {disabledInputTooltip}
                        {!shouldNotAllowUgcStreams && (
                          <PrimaryAnchor
                            className='cu-text-link-primary'
                            href={GRAPH_API_LEARN_MORE_LINK}
                            target='_blank'
                          >
                            &nbsp;Learn More
                          </PrimaryAnchor>
                        )}
                      </div>
                    }
                    isDisabled={!shouldDisableInput}
                    target={
                      <TooltipTargetContainer>
                        <StyledTextInput
                          disabled={
                            hasOutstandingSaveRequest || shouldDisableInput
                          }
                          onChange={(value) => {
                            handleSourceValueChange(value);
                          }}
                          onKeyDown={onKeyDown}
                          placeholder={placeholderText}
                          type='text'
                          value={value}
                        />
                      </TooltipTargetContainer>
                    }
                  />
                </FullWidthInputWrapper>
              </StyledSearchBar>
            )}
          </SourceValueContainer>
          <SmallTooltip
            content={`Can't add anymore hashtags. You've reached the limit.`}
            isDisabled={!shouldDisableHashtagAdd}
            target={
              <Button
                className='cu-button-secondary'
                isDisabled={
                  hasOutstandingSaveRequest ||
                  (!isGraphApiConnected && shouldDisableInput) ||
                  shouldDisableHashtagAdd ||
                  !value
                }
                onClick={handleAdd}
              >
                Add
              </Button>
            }
          />
        </StreamValueInputWrapper>
      </div>
      {shouldShowHashtagLimitAlert && (
        <HashtagLimitAlert type='warning'>
          {`You have ${remainingHashtagCount} remaining hashtags you can use. `}
          {areSocialDashboardsEnabled && (
            <PrimaryAnchor
              className='cu-text-link-primary'
              href={isShareV2Vendor ? '/connect/hashtags-usage' : '/instagram'}
              target='_blank'
            >
              View Hashtags Usage
            </PrimaryAnchor>
          )}
        </HashtagLimitAlert>
      )}
      <LoadingBar className='cu-mt-3' shouldShowLoader={isLoadingSources}>
        {hasSourceErrors ? (
          renderErrors(errors)
        ) : (
          <StreamSourcesList
            addedSources={addedSources}
            defaultPillType={StreamSourcePillType.ADDED_SOURCE}
            hasOutstandingSaveRequest={hasOutstandingSaveRequest}
            removeSource={(source) =>
              void dispatch(streamCreationActions.removeSelectedSource(source))
            }
          />
        )}
      </LoadingBar>
    </>
  );
};

const renderSelectItems = (itemsList: ISourceTypeSelectItem[]) => {
  return itemsList.map((item) => {
    return (
      <SelectItem key={item.value} label={item.label} value={item.value} />
    );
  });
};

const renderErrors = (errors: IStreamCreationErrors) => {
  return (
    <>
      {errors.sourceTypeError && <Warning>{errors.sourceTypeError}</Warning>}
      {errors.sourceError && <Warning>{errors.sourceError} </Warning>}
    </>
  );
};

const computeSourceTypeSelectItems = () => {
  return StreamCreationSourceTypes.map((type) => {
    return {
      label: StreamSourceTypeLabels[type],
      value: type,
    } as const;
  });
};

const AddContentHeader = styled.div`
  ${Mixins.Text.UiCopyBold}
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacings[2]};
`;

const TypeHeader = styled.div`
  ${Mixins.Text.Body}
  margin-bottom: ${({ theme }) => theme.spacings[2]};
`;

const StreamSourceSelectWrapper = styled.div`
  flex: 2;

  .cu-popover-target {
    width: 100%;
  }
`;

const StreamValueInputWrapper = styled.div`
  flex: 3;
  display: flex;
  align-items: flex-end;
`;

const SourceValueContainer = styled.div`
  margin-right: ${({ theme }) => theme.spacings[2]};
  flex-grow: 2;
`;

// Span hack ensures text overflow works properly on the InstagramLinkoutButton
const SourceHeader = styled.div`
  ${Mixins.Text.Body}
  margin-bottom: ${({ theme }) => theme.spacings[2]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 250px;

  span {
    min-width: 0;
  }
`;

const Warning = styled.div`
  color: ${({ theme }) => theme.colors.aliases.error};
  margin-bottom: ${({ theme }) => theme.spacings[1]};
`;

const FullWidthInputWrapper = styled.div`
  .cu-popover-target {
    display: block;
  }

  input {
    width: 100%;
  }
`;

const HashtagLimitAlert = styled(Alert)`
  display: flex;

  && {
    margin-top: ${({ theme }) => theme.spacings[3]};
    margin-bottom: ${({ theme }) => theme.spacings[2]};
  }
`;

const TooltipTargetContainer = styled.div`
  display: flex;
`;

const MultiHandleTargetContainer = styled.div`
  width: 100%;
`;

const StyledSearchBar = styled(SearchBar)`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledTextInput = styled(TextInput)`
  position: static;
`;

export default StreamsSourceSelector;
